import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue'
  import ModuleLessonLayoutLoader from '@/components/loaders/ModuleLessonLayoutLoader.vue'
  import ModuleLessonsSelector from '@/components/course/ModuleLessonsSelector.vue'
  import { CourseModule } from '@/store/modules/course'
  import useCourse from '@/composables/useCourse'
  import useSegment from '@/composables/useSegment'
  import useBreakpoint from '@/composables/useBreakpoint'
  import CookieHelper from '@/helpers/CookieHelper'
  import { EditorModule } from '@/store/modules/editor'
  import useToursAndHints from '@/composables/useToursAndHints'
  import useEditor from '@/composables/useEditor'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import useIcons from '@/composables/useIcons'
  import EditorButtons from '@/components/editor/EditorButtons.vue'
  import CourseSearchResults from '@/components/course/CourseSearchResults.vue'
  import FollowupLesson from '@/components/course/followup/FollowupLesson.vue'
  import { v4 as uuidv4 } from 'uuid'
  import GoogleReviewWidget from '@/components/GoogleReviewWidget.vue'
  import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router/composables'
  import eventBus from '@/main'
  import useI18n from '@/composables/useI18n'
  import type { CourseModule as CourseModuleState } from '@/services/interfaces/Course'
  import LanguageTranslateToggle from '@/components/common/LanguageTranslateToggle.vue'
  import useCommonMixin from '@/composables/useCommonMixin'
  import CourseExpiredBanner from '@/components/course/CourseExpiredBanner.vue'
  import useShopifyClient from '@/composables/useShopifyClient'
  import StartCourseExamCTA from '@/components/course/StartCourseExamCTA.vue'
  import InTrialCourseTimer from '@/components/course/InTrialCourseTimer.vue'
  import { UserModule } from '@/store/modules/user'
  import CommonApi from '@/services/api/CommonApi'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'ModuleLessonLayout',
  props: {
    searchTerm: {
      type: String,
      default: '',
    },
  },
  setup(__props) {

  

  const route = useRoute()
  const router = useRouter()
  const { translateString } = useI18n()
  const {
    currentCourseId,
    lessonId,
    moduleId,
    courseModules,
    currentLesson,
    currentLessonQuestions,
    currentModule,
    currentModuleLessonsLength,
    course,
    fetchingEditorDependencies,
    isPurchasedCourse,
    isTrialCourse,
    showNextCTA,
    nextSiblingModule,
    nextCourseModule,
    onModulePage,
    trackCoursePurchaseIntended,
    currentLessonIndex,
    productPrice,
    courseTrialPeriodExpired,
    showInTrialCourseTimer,
  } = useCourse()
  const { showOrHideToursAndHints, hideAllHints } = useToursAndHints()
  const { startModuleButtonClicked } = useSegment()
  const { isDesktopDevice, isMobileDevice, isLargeDesktop } = useBreakpoint()
  const { isNewLesson, inEditorMode, lessonSlideshows, lessonAudioFiles, isEditor } = useEditor(route)
  const { arrowRight, menuIcon } = useIcons()
  const { showTranslateSelector } = useCommonMixin()
  const { createShopifyCheckout } = useShopifyClient()

  const lessonCTA = ref('')
  const mobileSelector = ref(false)
  const currentCourseGroupId = ref('')

  const onLessonPage = computed((): boolean => !!(lessonId.value && moduleId.value && currentCourseId.value))

  const showExamButton = computed(() => {
    return isMobileDevice.value && course.value?.exam && (isPurchasedCourse.value || isTrialCourse.value)
  })

  const showPromoteBuyCourseCTA = computed(
    () => isMobileDevice.value && (course.value?.trial || !course.value?.purchased) && course.value?.shopifyProductId,
  )

  const nextModuleOrCourseDetails = (nextModule: CourseModuleState | null) => {
    if (nextModule) {
      lessonCTA.value = translateString('courses.sideshow.nextModule')
    } else {
      lessonCTA.value = translateString('courses.toCourseDetails')
    }
  }

  const setLessonCTA = () => {
    const nextModule = nextCourseModule(CourseModule.currentCourseGroupId)
    if (currentLessonIndex.value === currentModuleLessonsLength.value - 1) {
      if (currentModule.value?.guidedQuiz && !course.value?.expired) {
        lessonCTA.value = translateString('courses.sideshow.toGuidedQuiz')
      } else {
        const siblingIsFree = nextSiblingModule.value(currentCourseId.value, CourseModule.currentCourseGroupId)?.isFree
        if (isTrialCourse.value && !siblingIsFree) {
          lessonCTA.value = translateString('courses.buyCourse')
        } else {
          nextModuleOrCourseDetails(nextModule)
        }
      }
    } else {
      if (route.name === 'GuidedQuiz') {
        nextModuleOrCourseDetails(nextModule)
      } else {
        lessonCTA.value = translateString('courses.sideshow.nextLesson')
      }
    }
  }

  watch(lessonId, setLessonCTA)

  const areModulesAvailable = computed(() => !!CourseModule.coursesModuleMap[currentCourseId.value])
  watch(areModulesAvailable, () => {
    setLessonCTA()
    CourseModule.setCurrentModule(currentModule.value as CourseModuleState)
    gotoCourseDetailsPage()
  })

  const fetchingCourseModules = computed(() => courseModules.value === undefined)
  if (fetchingCourseModules.value) CourseModule.getCourseModules(currentCourseId.value)

  const fetchingCourse = computed(() => course.value === null)
  const purchasedCourseIds = CookieHelper.getCookieValue('purchased_course_id')
  if (purchasedCourseIds || fetchingCourse.value) {
    CourseModule.getCourse({ courseId: currentCourseId.value }).then((course) => {
      setLessonCTA()
      purchasedCourseIds?.forEach((courseId: string) => {
        if (courseId === course.id) {
          if (course.purchased) {
            CookieHelper.removeCookie('purchased_course_id')
          } else {
            CourseModule.course!.purchased = true
          }
        }
      })
    })
  }

  const startModule = () => {
    if (UserModule.hasValidUser && UserModule.email === 'ndoehn+test@gmail.com') {
      CommonApi.postSlackMessage(`
         🚀 Starting module:
         Course: ${course.value?.title} (${currentCourseId.value})
         Module: ${currentModule.value?.name} (${currentModule.value?.id})
         Lessons: ${currentModule.value?.lessons?.length}
         Has Quiz: ${!!currentModule.value?.guidedQuiz}
       `)
    }

    startModuleButtonClicked(
      currentCourseId.value,
      course.value?.title || '',
      currentModule.value.id,
      currentModule.value.name,
    )

    if (currentModule.value.lessons.length) {
      if (UserModule.hasValidUser && UserModule.email === 'ndoehn+test@gmail.com') {
        CommonApi.postSlackMessage(`
          ➡️ Routing to first lesson: ${currentModule.value.lessons[0].id}
          Module: ${currentModule.value.name}
        `)
      }
      return router
        .push({
          name: 'Lesson',
          params: { lessonId: currentModule.value.lessons[0].id, moduleId: currentModule.value.id },
        })
        .catch((error) => {
          if (UserModule.hasValidUser && UserModule.email === 'ndoehn+test@gmail.com') {
            CommonApi.postSlackMessage(`
              ❌ Failed to navigate to first lesson:
              Module: ${currentModule.value?.name}
              Lesson: ${currentModule.value?.lessons[0].id}
              Error: ${error.message}
            `)
          }
          return
        })
    }

    if (currentModule.value?.guidedQuiz) {
      if (UserModule.hasValidUser && UserModule.email === 'ndoehn+test@gmail.com') {
        CommonApi.postSlackMessage(`
          🎯 Routing to guided quiz:
          Module: ${currentModule.value.name} (${currentModule.value.id})
        `)
      }
      return router
        .push({
          name: 'GuidedQuiz',
          params: { courseId: currentCourseId.value, moduleId: currentModule.value.id },
        })
        .catch((error) => {
          if (UserModule.hasValidUser && UserModule.email === 'ndoehn+test@gmail.com') {
            CommonApi.postSlackMessage(`
              ❌ Failed to navigate to guided quiz:
              Module: ${currentModule.value?.name}
              Error: ${error.message}
            `)
          }
          return
        })
    }

    if (UserModule.hasValidUser && UserModule.email === 'ndoehn+test@gmail.com') {
      CommonApi.postSlackMessage(`
        ⚠️ No lessons or guided quiz found, proceeding to next module
        Current Module: ${currentModule.value?.name}
      `)
    }
    return nextLessonOrModule()
  }

  const nextLessonOrModule = () => {
    if (UserModule.hasValidUser && UserModule.email === 'ndoehn+test@gmail.com') {
      CommonApi.postSlackMessage(`
        🔄 Attempting next lesson/module:
        Current Module: ${currentModule.value?.name}
        Current Lesson: ${currentLesson.value?.title}
        Current Index: ${currentLessonIndex.value}
      `)
    }

    if (!currentModule.value) return

    if (currentModule.value.lessons.length) {
      if (currentLessonIndex.value === currentModule.value.lessons.length - 1) {
        if (currentModule.value?.guidedQuiz) {
          return router
            .push({
              name: 'GuidedQuiz',
              params: { courseId: currentCourseId.value, moduleId: currentModule.value.id },
            })
            .catch(() => {
              if (UserModule.hasValidUser && UserModule.email === 'ndoehn+test@gmail.com') {
                CommonApi.postSlackMessage(`
                  ❌ Failed to navigate to guided quiz after last lesson:
                  Module: ${currentModule.value?.name}
                `)
              }
              return
            })
        }

        return handleNextModule()
      }

      return handleRouteToLesson(currentLessonIndex.value + 1)
    }
    return handleNextModule()
  }

  const previousLesson = () => {
    /*
     just a fail proof check
     we only listen to this event if there is an actual previous lesson in the current module
     */
    if (currentLessonIndex.value > 0) {
      handleRouteToLesson(currentLessonIndex.value - 1)
    }
  }

  const handleNextModule = () => {
    const module = nextCourseModule(CourseModule.currentCourseGroupId)
    if (module) {
      return router
        .push({
          name: 'CourseModule',
          params: { courseId: currentCourseId.value, moduleId: module.id || '' },
        })
        .catch(() => {
          return
        })
    } else {
      return router.push({ name: 'CourseDetails' }).catch(() => {
        return
      })
    }
  }

  const handleRouteToLesson = (lessonIndex: number) => {
    if (!currentModule.value?.lessons?.[lessonIndex]?.id) {
      console.error('Unable to route to lesson - missing required data:', {
        moduleId: currentModule.value?.id,
        lessons: currentModule.value?.lessons,
        lessonIndex,
      })
      return
    }

    router
      .push({
        name: 'Lesson',
        params: {
          lessonId: currentModule.value.lessons[lessonIndex].id,
          moduleId: currentModule.value.id,
        },
      })
      .catch((err) => {
        console.error('Failed to navigate to lesson:', err)
        return
      })
  }

  watch(moduleId, async () => {
    EditorModule.setCurrentModule(null)
    CourseModule.setCurrentModule(null)
    CourseModule.setCurrentModule(currentModule.value as CourseModuleState)
    if (inEditorMode.value) {
      const curModules = structuredClone(courseModules.value || []) as CourseModuleState[]
      updateEditorStore(currentModule.value as CourseModuleState, curModules)
    }
  })

  watch(
    () => EditorModule.editMode,
    (inEditMode: boolean) => {
      if (inEditMode && currentLesson.value !== null) {
        CourseModule.getCurrentLessonQuestions({
          courseId: currentCourseId.value,
          moduleId: moduleId.value,
          lessonId: lessonId.value,
        })
      }
    },
  )

  const updateEditorStore = (curMod: CourseModuleState, curModules: CourseModuleState[]) => {
    EditorModule.setCurrentLesson(currentLesson.value)
    // if current lesson is a new lesson and is yet to be added to the module it belongs to
    if (isNewLesson.value && !curMod.lessons.some((l) => l.id === currentLesson.value!.id)) {
      curMod.lessons.push(currentLesson.value!)
    }
    EditorModule.setCurrentModule(curMod)
    EditorModule.setCourseModules({ courseId: currentCourseId.value, modules: curModules })
    EditorModule.setCurrentLessonQuestions(currentLessonQuestions.value)
    EditorModule.setLeuckentext(currentLesson.value?.leuckentext ?? null)
    EditorModule.setLessonSlideshows(currentLesson.value?.slideShow ?? null).then(() => {
      lessonSlideshows.value?.forEach((slide) => {
        eventBus.$set(slide, 'editorId', uuidv4())
        eventBus.$set(slide, 'slideImageData', '')
      })
    })
    EditorModule.setCurrentLessonAudioFiles(currentLesson.value?.audioFiles ?? null).then(() => {
      lessonAudioFiles.value?.forEach((audio) => {
        eventBus.$set(audio, 'editorId', uuidv4())
      })
    })
    EditorModule.setLessonDownloadable(currentLesson.value?.downloadables ?? null)
  }

  const resetEditorStore = (curMod: CourseModuleState) => {
    const lastLesson = curMod?.lessons.slice(-1)[0]
    if (isNewLesson.value && curMod) {
      if (lastLesson) {
        router.replace({
          name: 'Lesson',
          params: { lessonId: lastLesson.id, moduleId: curMod.id },
        })
      } else {
        router
          .push({
            name: 'CourseModule',
            params: { courseId: currentCourseId.value, moduleId: curMod.id },
          })
          .catch(() => {
            return
          })
      }
    }
    EditorModule.setCurrentLesson(null)
    EditorModule.setCurrentModule(null)
    EditorModule.setCourseModules({ courseId: currentCourseId.value, modules: [] })
    EditorModule.setCurrentLessonQuestions(null)
    EditorModule.setCurrentLessonAudioFiles(null)
    EditorModule.setLessonDownloadable(null)
  }

  watch(
    () => EditorModule.editMode && !fetchingEditorDependencies.value,
    (inEditMode: boolean) => {
      const curModules = structuredClone(courseModules.value || []) as CourseModuleState[]
      const curMod = curModules.find((m) => m.id === moduleId.value) as CourseModuleState

      if (inEditMode) {
        updateEditorStore(curMod, curModules)
        setTimeout(() => hideAllHints(), 10000)
      } else {
        if (!EditorModule.editMode) resetEditorStore(curMod)
        if (isDesktopDevice.value) showOrHideToursAndHints()
      }
    },
  )

  const gotoCourseDetailsPage = () => {
    if (currentModule.value?.disabled && !inEditorMode.value) {
      router.push({ name: 'CourseDetails' }).catch(() => {
        return
      })
    }
  }

  const removeUnsavedNewModule = () => {
    const index = CourseModule.coursesModuleMap[currentCourseId.value]!.findIndex((mod) => mod.id === moduleId.value)
    if (index > -1) {
      CourseModule.coursesModuleMap[currentCourseId.value]!.splice(index, 1)
    }
  }

  const removeUnsavedNewLesson = () => {
    const courseModule = CourseModule.coursesModuleMap[currentCourseId.value]!.find(
      (m) => m.id === moduleId.value,
    ) as CourseModuleState
    const lessonIndex = courseModule.lessons!.findIndex((lesson) => lesson.id === lessonId.value)
    if (lessonIndex > -1) {
      courseModule.lessons.splice(lessonIndex, 1)
    }
  }

  const shopifyCheckout = () => {
    trackCoursePurchaseIntended()
    createShopifyCheckout()
  }

  onBeforeMount(() => gotoCourseDetailsPage())
  onMounted(() => {
    if (areModulesAvailable.value) {
      setLessonCTA()
      CourseModule.setCurrentModule(currentModule.value as CourseModuleState)
    }
  })
  onUnmounted(() => {
    CourseModule.setCurrentCourseGroup('')
  })

  onBeforeRouteLeave((_to, _from, next) => {
    const { isNewModule, isNewLesson } = useEditor(route)
    if (isNewModule.value) {
      removeUnsavedNewModule()
    }
    if (isNewLesson.value) {
      removeUnsavedNewLesson()
    }
    next()
  })

  onBeforeRouteUpdate((_to, _from, next) => {
    const { hasChanges, inEditorMode, discardAllChanges, saveAllChanges, isNewLesson, isNewModule } = useEditor(route)
    if (
      !inEditorMode ||
      (inEditorMode.value &&
        (!EditorModule.currentLesson || !EditorModule.currentLesson.id.startsWith('new-')) &&
        isNewLesson.value) ||
      (inEditorMode.value &&
        (!EditorModule.currentModule || !EditorModule.currentModule.id.startsWith('mod-')) &&
        isNewModule.value)
    ) {
      return next()
    }
    if (hasChanges.value) {
      eventBus.$emit('show-modal', {
        modalContentComponent: 'ConfirmationModal',
        modalTitle: translateString('editor.thereAreChanges'),
        modalProps: {
          confirmationText: translateString('editor.saveOrDiscard'),
          cancelButtonText: translateString('editor.discardChanges'),
          okButtonText: translateString('editor.saveAllChanges'),
          showSvgs: true,
          okCallback: async (closeCallback: () => void) => {
            saveAllChanges()
            next()
            if (typeof closeCallback === 'function') closeCallback()
          },
          cancelCallback: () => {
            discardAllChanges()
            next()
          },
        },
        cssClass: 'confirmation editor',
        modalCloseCallback: (callback: () => void) => {
          if (typeof callback === 'function') callback()
        },
      })
    } else {
      next()
    }
  })

return { __sfc: true,route, router, translateString, currentCourseId, lessonId, moduleId, courseModules, currentLesson, currentLessonQuestions, currentModule, currentModuleLessonsLength, course, fetchingEditorDependencies, isPurchasedCourse, isTrialCourse, showNextCTA, nextSiblingModule, nextCourseModule, onModulePage, trackCoursePurchaseIntended, currentLessonIndex, productPrice, courseTrialPeriodExpired, showInTrialCourseTimer, showOrHideToursAndHints, hideAllHints, startModuleButtonClicked, isDesktopDevice, isMobileDevice, isLargeDesktop, isNewLesson, inEditorMode, lessonSlideshows, lessonAudioFiles, isEditor, arrowRight, menuIcon, showTranslateSelector, createShopifyCheckout, lessonCTA, mobileSelector, currentCourseGroupId, onLessonPage, showExamButton, showPromoteBuyCourseCTA, nextModuleOrCourseDetails, setLessonCTA, areModulesAvailable, fetchingCourseModules, fetchingCourse, purchasedCourseIds, startModule, nextLessonOrModule, previousLesson, handleNextModule, handleRouteToLesson, updateEditorStore, resetEditorStore, gotoCourseDetailsPage, removeUnsavedNewModule, removeUnsavedNewLesson, shopifyCheckout, computed, onBeforeMount, onMounted, onUnmounted, ref, watch, ModuleLessonLayoutLoader, ModuleLessonsSelector, CourseModule, useCourse, useSegment, useBreakpoint, CookieHelper, EditorModule, useToursAndHints, useEditor, KetchUpButton, SVGRenderer, useIcons, EditorButtons, CourseSearchResults, FollowupLesson, uuidv4, GoogleReviewWidget, onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter, eventBus, useI18n, LanguageTranslateToggle, useCommonMixin, CourseExpiredBanner, useShopifyClient, StartCourseExamCTA, InTrialCourseTimer, UserModule, CommonApi }
}

})